/**
 * Fase D (família): els missatges de bon dia de la mare, l'hort del balcó i el pastís chiffon.
 * Les frases marcades com a «pràctica d'anglès» es deixen en anglès expressament en tots els idiomes.
 */
import type { Messages } from './en';

export const caFamily: Pick<Messages, 'family'> = {
  family: {
    mapNames: { balcony: 'Balcó' },
    zones: { balcony: 'Hort del balcó' },
    objects: {
      fam_plot: 'Jardinera',
      fam_tap: 'Aixeta',
    },
    npc: {},
    items: {
      tomato: { name: 'Tomàquet', desc: 'Sucós i tebi de sol, del teu balcó. Menja-te\'l per guanyar +20 d\'energia.' },
      sunflower: { name: 'Gira-sol', desc: 'Una flor grossa i somrient que has fet créixer tu. Només de mirar-la, l\'ànim et puja a 100.' },
      chiffon: { name: 'Pastís chiffon familiar', desc: 'Un 戚風蛋糕 alt i esponjós fet amb tres maduixes del teu hort. Comparteix-lo a casa o dona\'t un caprici.' },
    },

    // --- Missatges de la mare ----------------------------------------------------------
    menuMom: '📩 Missatges de la mare',
    toastNew: '📩 La mare t\'ha enviat un missatge!',
    previewNone: 'Encara no hi ha missatges',
    you: 'Tu',
    today: 'Avui',
    sad: '😢',
    loveUp: (n: number) => `Mare ❤️ +${n}`,
    loveDown: 'La mare sembla una mica trista…',
    ps: {
      water: 'P. D.: No t\'oblidis de regar les plantes del balcó 🌱',
      ripe: 'P. D.: Em sembla que al balcó hi ha alguna cosa a punt per collir! 🍅🍓',
    },
    /**
     * Missatges de la mare. `msgs` són seus ("@card" = una imatge de bon dia); `replies` són
     * les opcions del jugador (pràctica d'anglès), cadascuna amb les respostes de la mare i un torn opcional.
     */
    texts: {
      rain: {
        icon: '☔',
        msgs: ['Bon dia, carinyo ☔', 'Avui plourà. Agafa el paraigua! El blau és al costat del sabater.'],
        replies: [
          {
            me: 'Got it, Mom! I’ll take the umbrella ☂️', tone: 'warm', love: 5,
            mom: ['Així m\'agrada 👍', 'I res de saltar als bassals amb les sabates de l\'escola. Que et conec 😏'],
            next: [
              { me: 'Just ONE little puddle? 🥺', tone: 'warm', love: 3, mom: ['Hahaha. D\'acord, un. Un de ben petitó.', 'I després deixa les sabates assecant-se al costat de la porta!'] },
              { me: 'I promise. No puddles!', tone: 'ok', mom: ['M\'ho creuré quan vegi els mitjons secs 🧦😆'] },
            ],
          },
          {
            me: 'It’s only a little rain. I’ll be fine.', tone: 'ok',
            mom: ['A Taiwan, una mica de pluja es converteix en un xàfec en cinc minuts! 🌧️', 'Emporta-te\'l, si us plau. Per mi?'],
            next: [
              { me: 'Okay, okay, I’ll take it. For you ❤️', tone: 'warm', love: 4, mom: ['Gràcies, bonic. Ara puc treballar tranquil·la 😊'] },
              { me: 'Mom, you worry too much. Stop it.', tone: 'rude', mom: ['…D\'acord.', 'Les mares estem fetes per patir. Truca\'m si et mulles.'] },
            ],
          },
          {
            me: 'Ugh, umbrellas are so annoying.', tone: 'rude',
            mom: ['Més pesat és posar-se malalt…', 'La teva germana petita s\'emporta l\'impermeable. Fins i tot ella em fa cas!'],
            next: [
              { me: 'Sorry, Mom. I’ll take it.', tone: 'warm', love: 3, mom: ['Gràcies 🥰 Aquest és el meu nen.'] },
              { me: 'Fine… but it’s ugly.', tone: 'ok', mom: ['Doncs compra\'t un de bonic amb la teva setmanada! 😂'] },
            ],
          },
        ],
      },
      storm: {
        icon: '⛈️',
        msgs: ['⛈️ Tempestes aquesta tarda!', 'El paraigua és a la motxilla. Si hi ha llamps, espera a dins. No et posis sota els arbres!'],
        replies: [
          {
            me: 'I’ll wait inside until it stops. Promise! ⛈️', tone: 'warm', love: 6,
            mom: ['Que n\'és de llest el meu nen 🧠✨', 'Si es posa molt lleig, truca\'m i et vinc a buscar.'],
            next: [
              { me: 'Thanks, Mom. Can we have hot soup tonight? 🍲', tone: 'warm', love: 3, mom: ['Sopa de blat de moro, la teva preferida. Tracte fet 😋'] },
              { me: 'Can Dad come instead? His car has music 🎶', tone: 'ok', mom: ['Hum. La meva moto també té música. Canto jo! 🛵🎤😂'] },
            ],
          },
          {
            me: 'Thunder is cool! Can I watch the lightning?', tone: 'ok',
            mom: ['Des de la FINESTRA, sí. Des del pati, NO ⚡', 'Els llamps no són un vídeo, carinyo 😅'],
            next: [
              { me: 'Haha, okay. From the window. Love you!', tone: 'warm', love: 4, mom: ['Jo t\'estimo més ❤️ No et mullis!'] },
              { me: 'You never let me do anything fun.', tone: 'rude', mom: ['Et deixo fer moltes coses divertides… només que no les perilloses.', 'Aquesta nit mirem la tempesta junts, amb alguna cosa per picar. D\'acord?'] },
            ],
          },
          {
            me: 'I’m not a baby. Stop texting me.', tone: 'rude',
            mom: ['D\'acord. És que t\'estimo, només això.'],
            next: [{ me: 'Sorry, Mom… I love you too.', tone: 'warm', love: 4, mom: ['🥹 D\'acord. Au, vés, que faràs tard!'] }],
          },
        ],
      },
      sunny: {
        icon: '☀️',
        msgs: ['Bon dia! ☀️ Avui fa sol i caloreta.', 'Beu molta aigua! L\'ampolla és plena — no me la tornis a portar plena 😤'],
        replies: [
          {
            me: 'I’ll drink it ALL. Every drop! 💧', tone: 'warm', love: 5,
            mom: ['Hahaha. Aquesta nit revisaré l\'ampolla 🕵️‍♀️'],
            next: [
              { me: 'Deal! And I’ll fill it again at school.', tone: 'warm', love: 3, mom: ['Ostres. Qui ets i què has fet amb el meu fill? 😂❤️'] },
              { me: 'What do I get if it’s empty?', tone: 'ok', mom: ['Una abraçada. I potser un te amb perles diumenge 🧋', 'Amb la meitat de sucre!'] },
            ],
          },
          {
            me: 'But the school water tastes funny.', tone: 'ok',
            mom: ['És aigua i prou! Bé… posa-hi una rodanxa de llimona.', 'Hi ha llimones a la nevera 🍋'],
            next: [
              { me: 'Lemon water! Good idea, thanks Mom 🍋', tone: 'warm', love: 4, mom: ['Estic plena de bones idees. Digues-li al teu pare 😎'] },
              { me: 'Lemon water is gross.', tone: 'rude', mom: ['L\'àvia fa 80 anys que beu aigua amb llimona…', 'D\'acord, doncs aigua sola. Però BEU-TE-LA!'] },
            ],
          },
          {
            me: 'Water is boring. I want soda.', tone: 'rude',
            mom: ['Els refrescos porten MOLTÍSSIM sucre…', 'Les dents et ploraran. I després ploraré jo.'],
            next: [{ me: 'Okay, water. For my teeth and for you 😁', tone: 'warm', love: 3, mom: ['Molt bé! Aquesta nit m\'ensenyes aquestes dents brillants 😁🦷'] }],
          },
        ],
      },
      hot: {
        icon: '🥵',
        msgs: ['🥵 Avui arribarem a 33 graus!', 'Posa\'t la gorra, beu aigua a cada pati i queda\'t a l\'ombra al migdia.'],
        replies: [
          {
            me: 'Hat on! I look super cool 🧢', tone: 'warm', love: 5,
            mom: ['El nen més guai de Taiwan 😎', 'I crema solar! És al prestatge de l\'entrada.'],
            next: [
              { me: 'Got it! Sunscreen, hat, water ✅', tone: 'warm', love: 3, mom: ['Llista perfecta! La mestra Mare et posa un 100 💯'] },
              { me: 'Sunscreen is sticky…', tone: 'ok', mom: ['Millor enganxós que vermell com una gamba 🦐😂'] },
            ],
          },
          {
            me: 'Can I buy a shaved ice after school? 🍧', tone: 'ok',
            mom: ['Mmm… un granissat de mango petit.', 'Però primer buida l\'ampolla d\'aigua!'],
            next: [
              { me: 'Yes! Thank you, best Mom ever! 🥭', tone: 'warm', love: 4, mom: ['La millor mare del món també en vol una cullerada 😋'] },
              { me: 'Can I get a BIG one?', tone: 'ok', mom: ['Petit. La teva panxeta és petita 😆'] },
            ],
          },
          {
            me: 'I hate hats. I’m not wearing it.', tone: 'rude',
            mom: ['Doncs el sol et farà venir mal de cap…', 'Almenys queda\'t a l\'ombra, si us plau?'],
            next: [{ me: 'Sorry. I’ll wear it, Mom.', tone: 'warm', love: 3, mom: ['Gràcies, carinyo. A l\'agost m\'ho agrairàs 😂'] }],
          },
        ],
      },
      cool: {
        icon: '🍂',
        msgs: ['Bon dia 🍂 Avui refresca, només 18 graus.', 'Posa\'t la jaqueta! No portar-la a la mà — POSAR-TE-LA 🧥'],
        replies: [
          {
            me: 'Jacket is ON. Zipped up! 🧥', tone: 'warm', love: 5,
            mom: ['Que maco i que abrigadet 😍', 'També tens una beguda calentona al termo.'],
            next: [
              { me: 'Thanks, Mom! Is it ginger tea? 🫚', tone: 'warm', love: 3, mom: ['Te de gingebre amb sucre moreno. La recepta de l\'àvia ❤️'] },
              { me: 'Do I have to drink all of it?', tone: 'ok', mom: ['Sí. És màgic. Espanta els refredats 🪄😆'] },
            ],
          },
          {
            me: '18 degrees isn’t cold!', tone: 'ok',
            mom: ['Per a Taiwan és un FRED polar! 🥶😂', 'Tothom a l\'escola anirà amb plomes. Fes-me cas.'],
            next: [
              { me: 'Haha, okay. I’ll wear it so you don’t worry.', tone: 'warm', love: 4, mom: ['Gràcies, bonic. Mare contenta, mare callada 😌'] },
              { me: 'Down jackets are for old people.', tone: 'rude', mom: ['Jo porto plomes…', 'D\'acord, d\'acord. Almenys emporta-te-la, si us plau?'] },
            ],
          },
          {
            me: 'I don’t need a jacket. Leave me alone.', tone: 'rude',
            mom: ['Quan et refredis, qui farà la sopa? Jo.'],
            next: [{ me: 'Sorry, Mom… I’ll wear it.', tone: 'warm', love: 3, mom: ['Bé. I faré sopa igualment, per si de cas 🍲'] }],
          },
        ],
      },
      typhoon: {
        icon: '🌀',
        msgs: ['🌀 Dia de tifó! Ni escola ni feina: tothom a casa.', 'M\'ajudes a preparar-nos? Entra les plantes del balcó, omple ampolles d\'aigua i carrega la llanterna 🔦'],
        replies: [
          {
            me: 'Of course! I’ll help with everything 💪', tone: 'warm', love: 7,
            mom: ['El meu petit heroi 🦸', 'Primer les plantes, abans que bufi fort!'],
            next: [
              { me: 'On it! Plants first, then the water bottles.', tone: 'warm', love: 3, mom: ['Quin equip. Després fideus instantanis i cartes 🍜🃏'] },
              { me: 'Can we watch the typhoon from the window?', tone: 'ok', mom: ['Lluny del vidre, sí. Les finestres es poden trencar!', 'Primer cinta als vidres 🙂'] },
            ],
          },
          {
            me: 'A typhoon day! Can I play games all day? 🎮', tone: 'ok',
            mom: ['Hahaha. Tots els nens de Taiwan diuen el mateix 😂', 'Primer ajudes i després jugues. Tracte?'],
            next: [
              { me: 'Deal! Helping first 💪', tone: 'warm', love: 4, mom: ['Aquest és el meu nen! Et faré els teus fideus preferits 🍜'] },
              { me: 'No. It’s a holiday!', tone: 'rude', mom: ['No és festa per a la gent que arregla els cables de la llum…', 'Siguem agraïts i ajudem una mica, d\'acord?'] },
            ],
          },
          {
            me: 'That’s boring. Ask my sister.', tone: 'rude',
            mom: ['La teva germana té sis anys. No pot amb els testos.', 'Avui et necessito de debò.'],
            next: [{ me: 'Sorry. I’ll help you, Mom.', tone: 'warm', love: 4, mom: ['Gràcies, carinyo. Junts estarem segurs ❤️'] }],
          },
        ],
      },
      exam: {
        icon: '📝',
        msgs: ['Molta sort a l\'examen d\'avui! 📝', 'T\'he posat un zongzi a la carmanyola. «Bāo zhòng» vol dir que aprovaràs segur! 😆'],
        replies: [
          {
            me: 'Thanks, Mom! I studied a lot. I’ll do my best! 📚', tone: 'warm', love: 6,
            mom: ['Ho sé. Ahir a la nit vaig veure el teu llum encès 💡', 'Treguis el que treguis, estic orgullosa de tu.'],
            next: [
              { me: 'That means a lot. Love you ❤️', tone: 'warm', love: 3, mom: ['❤️❤️❤️ Ara ves i ensenya a aquest examen qui mana!'] },
              { me: 'Even if I get a 60?', tone: 'ok', mom: ['Encara que treguis un 60. Però un 61 és millor 😂', 'Llegeix cada pregunta dues vegades!'] },
            ],
          },
          {
            me: 'I’m so nervous…', tone: 'ok',
            mom: ['Respira fondo tres vegades. Agafa aire… i deixa\'l anar… 🌬️', 'Estar nerviós vol dir que t\'importa. Això és bo.'],
            next: [
              { me: 'Okay… breathing. Thanks, Mom. I feel better.', tone: 'warm', love: 5, mom: ['Tu pots! T\'animo des de la feina 📣'] },
              { me: 'What if I forget everything?', tone: 'ok', mom: ['No se t\'oblidarà. I si passa, és un examen, no tota la teva vida 🤗'] },
            ],
          },
          {
            me: 'Don’t remind me! You’re making it worse!', tone: 'rude',
            mom: ['Perdona… només et volia donar ànims.'],
            next: [{ me: 'Sorry, Mom. I’m just stressed. Thank you.', tone: 'warm', love: 4, mom: ['Ho sé, carinyo. Una abraçada enorme 🤗 Poder zongzi!'] }],
          },
        ],
      },
      dinner: {
        icon: '🍗',
        msgs: ['Bon dia! A que no saps què hi ha per sopar? 😋', 'Pollastre de les tres tasses! 三杯雞 — amb molta alfàbrega 🌿'],
        replies: [
          {
            me: 'YES! My favorite! Can I help you cook? 🍗', tone: 'warm', love: 6,
            mom: ['És clar! Tu culls l\'alfàbrega i remenes l\'olla.', 'A les sis a casa, xef! 👩‍🍳'],
            next: [
              { me: 'Chef reporting for duty at six! 🫡', tone: 'warm', love: 3, mom: ['Hahaha, xef! Et buscaré un davantal 😆'] },
              { me: 'Can I just do the tasting part? 😋', tone: 'ok', mom: ['Qui tasta, renta els plats 😏'] },
            ],
          },
          {
            me: 'What are the three cups?', tone: 'ok',
            mom: ['Oli de sèsam, vi d\'arròs i salsa de soja: una tassa de cada! 🍶', 'Ara ja saps un secret de família 🤫'],
            next: [
              { me: 'Cool! Will you teach me the recipe someday?', tone: 'warm', love: 4, mom: ['Algun dia? Aquest cap de setmana! 🥰'] },
              { me: 'Is there a cup of chocolate? 🍫', tone: 'ok', mom: ['Hahaha, no! Això són les postres, ximplet 😂'] },
            ],
          },
          {
            me: 'Chicken again? Boring.', tone: 'rude',
            mom: ['M\'aixeco d\'hora per comprar el pollastre al mercat…'],
            next: [
              { me: 'Sorry, Mom. I love your three-cup chicken. Really!', tone: 'warm', love: 4, mom: ['Hum… d\'acord. Et toca el tros més gros 😌'] },
              { me: 'Can we have pizza instead?', tone: 'ok', mom: ['Pizza dissabte. Avui, pollastre de les tres tasses. Normes de la mare 📏'] },
            ],
          },
        ],
      },
      card: {
        icon: '🌻',
        card: { theme: 1, big: 'Bon dia', sub: 'Pau i salut cada dia' },
        msgs: ['@card', 'Bon dia, vida meva 🌸🌸 Pau i salut cada dia! 🙏'],
        replies: [
          {
            me: 'Good morning, Mom! Pretty flowers 🌻', tone: 'warm', love: 5,
            mom: ['Oi que sí? Me la va enviar primer la tieta Lin 😆', 'L\'he reenviat a quaranta persones.'],
            next: [
              { me: 'Forty?! You’re the queen of good-morning pictures 👑', tone: 'warm', love: 3, mom: ['Gràcies, gràcies 👸 En tinc 300 més de guardades 😂'] },
              { me: 'Mom… you don’t have to send one every day 😅', tone: 'ok', mom: ['I llavors com sabràs que estic bé? 😂', 'Una imatge al dia = la mare està bé!'] },
            ],
          },
          {
            me: '@card', tone: 'warm', love: 5,
            mom: ['OSTRES! El meu fill M\'HA enviat una imatge! 🥹', 'L\'ensenyaré a tota la feina.'],
            next: [
              { me: 'Please don’t show everyone 😳', tone: 'ok', mom: ['Massa tard. La senyora Chen diu que ets un encant 😆'] },
              { me: 'Tell them I said hi! 👋', tone: 'warm', love: 2, mom: ['Tots et tornen la salutació! Ets famós 🌟'] },
            ],
          },
          {
            me: 'Not another one of those pictures 🙄', tone: 'rude',
            mom: ['Vaig trigar tres minuts a triar aquesta…'],
            next: [{ me: 'Sorry, Mom. It IS pretty 🌸', tone: 'warm', love: 3, mom: ['Hehe. Demà en triaré una amb un gat 🐱'] }],
          },
        ],
      },
    },
    /** Text de la resposta «enviar una imatge de tornada». */
    sendCard: 'Enviar-li una imatge de bon dia 🖼️',
    replyCard: { theme: 4, big: 'Bon dia', sub: 'T\'estimo, mare' },

    // --- Hort del balcó ----------------------------------------------------------------
    crops: {
      strawberry: { name: 'Maduixa', icon: '🍓', seed: 'Llavors de maduixa' },
      tomato: { name: 'Tomàquet', icon: '🍅', seed: 'Llavors de tomàquet' },
      sunflower: { name: 'Gira-sol', icon: '🌻', seed: 'Llavors de gira-sol' },
    },
    stages: ['Terra buida', 'Plançó', 'Brot', 'Creixent', 'Madura!'],
    plotTitle: (n: number) => `Jardinera ${n}`,
    emptyText: 'Soft, dark soil. What do you want to plant?', // pràctica d'anglès
    plant: (seed: string) => `🌱 Plantar ${seed}`,
    free: 'gratis',
    notNow: 'Ara no',
    noCoins: 'No tens prou monedes per a aquestes llavors.',
    planted: (crop: string) => `You planted ${crop}! Water it once a day.`, // pràctica d'anglès
    watered: (n: number) => `💧 Regat! (${n}/3)`,
    rainWatered: '🌧️ Avui la pluja ha regat les teves plantes.',
    alreadyWatered: 'La terra encara és humida. Torna demà!',
    canEmpty: 'La regadora és buida: omple-la a l\'aixeta.',
    harvest: (what: string) => `Harvest time! You picked ${what}.`, // pràctica d'anglès
    growText: (crop: string, stage: string, n: number) => `${crop} · ${stage} · regat ${n}/3 dies`,
    covered: 'Les plantes estan protegides sota la lona fins que passi el tifó.',
    tapTitle: 'Aixeta',
    tapText: 'Una aixeta vella i rovellada amb una galleda blava. La regadora és al costat.',
    fillCan: (n: number) => `🪣 Omplir la regadora (${n} regs)`,
    canFilled: 'Glu, glu… la regadora és plena!',
    fillBottle: '💧 Omplir l\'ampolla d\'aigua',
    bottleFilled: 'L\'ampolla torna a ser plena.',
    canState: (n: number) => `Regadora: en queden ${n}`,
    plantsSafe: '🪴 Les plantes del balcó estan a recer a dins. Ben pensat!',
    typhoonWarn: '🌀 Avui hi ha tifó! Entra les plantes del balcó abans que es faci de nit.',
    typhoonHit: '🌀 El tifó d\'ahir a la nit ha aixafat les plantes del balcó… hauran de tornar a créixer.',
    tomatoEat: 'Dolç i sucós! +20 d\'energia.',
    sunflowerLook: 'El gira-sol et somriu. Et sents de meravella!',

    // --- Forn: pastís chiffon -----------------------------------------------------------
    chiffon: '🎂 Fornejar un pastís chiffon familiar 戚風蛋糕',
    chiffonNeeds: 'Un pastís chiffon necessita 3 maduixes. Fes-les créixer al balcó!',
    chiffonWork: 'Separant els rovells, muntant el merengue, incorporant les maduixes…',
    chiffonDone: 'Alt i esponjós com un núvol! Un 戚風蛋糕 familiar!',
    chiffonShare: 'Tothom s\'hi aplega al voltant. «Que tou! Com un núvol!» Tota la família somriu.',
    chiffonEat: 'Et menges un bon tros esponjós. Felicitat pura!',

    journal: {
      harvest: 'Has collit {arg} de l\'hort del balcó',
      chiffon: 'Has fornejat un pastís chiffon familiar amb maduixes del teu hort',
    },
  },
};
